const getAdminBaseUrl = () => {
  const rawBase =
    process.env.REACT_APP_API_BASE_URL ||
    process.env.REACT_APP_API_URL ||
    "http://localhost:18009/admin";
  const trimmed = rawBase.replace(/\/+$/, "");
  return trimmed.endsWith("/admin") ? trimmed : `${trimmed}/admin`;
};

const getAuthHeaders = () => {
  const token =
    localStorage.getItem("token") || localStorage.getItem("adminToken");
  if (!token) {
    return { "Content-Type": "application/json" };
  }
  const authHeader =
    token.startsWith("Basic ") || token.startsWith("Bearer ")
      ? token
      : `Bearer ${token}`;
  return {
    "Content-Type": "application/json",
    Authorization: authHeader,
  };
};

const handleApiResponse = async (response) => {
  const contentType = response.headers.get("content-type");
  const isJson = contentType && contentType.includes("application/json");
  const result = isJson ? await response.json() : await response.text();

  if (!response.ok || (typeof result === "object" && result.status === false)) {
    const errorMsg =
      (typeof result === "object" &&
        (result?.error?.message ||
          result?.error ||
          result?.sqlMessage ||
          result?.message ||
          result?.details)) ||
      (typeof result === "string" ? result : "API request failed");
    throw new Error(errorMsg);
  }

  return result;
};

// ==========================================
// 🃏 1. BOOM CARDS API ENDPOINTS
// ==========================================

/**
 * Fetch Boom Cards Settings & Statistics
 * Endpoint: GET /admin/game/boomcards/settings (fallback: /admin/game/settings?game=boomcards)
 */
export const getBoomCardsSettings = async () => {
  const baseUrl = getAdminBaseUrl();
  const endpoints = [
    `${baseUrl}/game/boomcards/settings`,
    `${baseUrl}/game/settings?game=boomcards`,
    `${baseUrl}/game/boomcards`,
  ];

  let lastResponse;
  for (const url of endpoints) {
    try {
      const response = await fetch(url, {
        method: "GET",
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        return handleApiResponse(response);
      }
      lastResponse = response;
      if (response.status !== 404) {
        return handleApiResponse(response);
      }
    } catch (netErr) {
      if (url === endpoints[endpoints.length - 1]) {
        throw new Error(netErr.message || "Network error connecting to API");
      }
    }
  }

  return handleApiResponse(lastResponse);
};

/**
 * Update Boom Cards Settings
 * Endpoint: PUT /admin/game/boomcards/settings (fallback: /admin/game/settings?game=boomcards)
 * @param {Object} settingsData
 */
export const updateBoomCardsSettings = async (settingsData) => {
  const baseUrl = getAdminBaseUrl();
  const payload = {
    ...settingsData,
    game: "boomcards",
    gameType: "boomcards",
  };
  if (typeof payload.isActive === "boolean") {
    payload.isActive = payload.isActive ? 1 : 0;
  }

  const endpoints = [
    `${baseUrl}/game/boomcards/settings`,
    `${baseUrl}/game/settings?game=boomcards`,
    `${baseUrl}/game/boomcards`,
  ];

  let lastResponse;
  for (const url of endpoints) {
    try {
      const response = await fetch(url, {
        method: "PUT",
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      });
      if (response.ok) {
        return handleApiResponse(response);
      }
      lastResponse = response;
      if (response.status !== 404) {
        return handleApiResponse(response);
      }
    } catch (netErr) {
      if (url === endpoints[endpoints.length - 1]) {
        throw new Error(netErr.message || "Network error connecting to API");
      }
    }
  }

  return handleApiResponse(lastResponse);
};

/**
 * Get Boom Cards Audit History Logs
 * Endpoint: GET /admin/game/boomcards/history (fallback: /admin/game/history?game=boomcards)
 * @param {Object} params
 */
export const getBoomCardsHistory = async ({
  page = 1,
  perPage = 20,
  search = "",
  status = "",
} = {}) => {
  const baseUrl = getAdminBaseUrl();
  const queryParams = new URLSearchParams();

  if (page) queryParams.append("page", String(page));
  if (perPage) {
    queryParams.append("perPage", String(perPage));
    queryParams.append("limit", String(perPage));
  }
  if (search && search.trim()) queryParams.append("search", search.trim());
  if (status && status !== "all") queryParams.append("status", status.trim());

  const baseQuery = queryParams.toString() ? `?${queryParams.toString()}` : "";
  const genericQuery = queryParams.toString()
    ? `?${queryParams.toString()}&game=boomcards&gameType=boomcards`
    : "?game=boomcards&gameType=boomcards";

  const endpoints = [
    `${baseUrl}/game/boomcards/history${baseQuery}`,
    `${baseUrl}/game/history${genericQuery}`,
    `${baseUrl}/game/boomcards/logs${baseQuery}`,
  ];

  let lastResponse;
  for (const url of endpoints) {
    try {
      const response = await fetch(url, {
        method: "GET",
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        return handleApiResponse(response);
      }
      lastResponse = response;
      if (response.status !== 404) {
        return handleApiResponse(response);
      }
    } catch (netErr) {
      if (url === endpoints[endpoints.length - 1]) {
        throw new Error(netErr.message || "Network error connecting to API");
      }
    }
  }

  return handleApiResponse(lastResponse);
};

// ==========================================
// 🎰 1.5. 777 SLOTS API ENDPOINTS
// ==========================================

/**
 * Fetch 777 Slots Game Settings & Statistics
 * Endpoint: GET /admin/game/slots/settings (fallback: /admin/game/slots)
 */
export const getSlotsSettings = async () => {
  const baseUrl = getAdminBaseUrl();
  const endpoints = [
    `${baseUrl}/game/slots/settings`,
    `${baseUrl}/game/slots`,
    `${baseUrl}/game/settings?game=slots`,
    `${baseUrl}/game/settings/slots`,
  ];

  let lastResponse;
  for (const url of endpoints) {
    try {
      const response = await fetch(url, {
        method: "GET",
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        return handleApiResponse(response);
      }
      lastResponse = response;
      if (response.status !== 404) {
        return handleApiResponse(response);
      }
    } catch (netErr) {
      if (url === endpoints[endpoints.length - 1]) {
        throw new Error(netErr.message || "Network error connecting to API");
      }
    }
  }

  return handleApiResponse(lastResponse);
};

/**
 * Update 777 Slots Game Settings
 * Endpoint: PUT /admin/game/slots/settings (fallback: /admin/game/slots)
 * @param {Object} settingsData
 */
export const updateSlotsSettings = async (settingsData) => {
  const baseUrl = getAdminBaseUrl();
  const payload = {
    jackpotPoints: Number(settingsData.jackpotPoints ?? 500),
    tripleBarPoints: Number(settingsData.tripleBarPoints ?? 200),
    tripleCherryPoints: Number(settingsData.tripleCherryPoints ?? 100),
    otherTriplePoints: Number(settingsData.otherTriplePoints ?? 50),
    match2Points: Number(settingsData.match2Points ?? 20),
    totalSpins: Number(settingsData.totalSpins ?? 5),
    cooldownSeconds: Number(settingsData.cooldownSeconds ?? 180),
    isActive: typeof settingsData.isActive === "boolean" ? settingsData.isActive : Boolean(settingsData.isActive),
    game: "slots",
    gameType: "slots",
  };
  if (settingsData.name) payload.name = settingsData.name;
  if (settingsData.description) payload.description = settingsData.description;
  if (settingsData.pointProbabilities) payload.pointProbabilities = settingsData.pointProbabilities;

  const endpoints = [
    `${baseUrl}/game/slots/settings`,
    `${baseUrl}/game/slots`,
    `${baseUrl}/game/settings?game=slots`,
  ];

  let lastResponse;
  for (const url of endpoints) {
    try {
      const response = await fetch(url, {
        method: "PUT",
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      });
      if (response.ok) {
        return handleApiResponse(response);
      }
      lastResponse = response;
      if (response.status !== 404) {
        return handleApiResponse(response);
      }
    } catch (netErr) {
      if (url === endpoints[endpoints.length - 1]) {
        throw new Error(netErr.message || "Network error connecting to API");
      }
    }
  }

  return handleApiResponse(lastResponse);
};

/**
 * Get 777 Slots Audit History Logs
 * Endpoint: GET /admin/game/slots/history (fallback: /admin/game/history?game=slots)
 * @param {Object} params
 */
export const getSlotsHistory = async ({
  page = 1,
  perPage = 20,
  search = "",
  status = "",
} = {}) => {
  const baseUrl = getAdminBaseUrl();
  const queryParams = new URLSearchParams();

  if (page) queryParams.append("page", String(page));
  if (perPage) {
    queryParams.append("perPage", String(perPage));
    queryParams.append("limit", String(perPage));
  }
  if (search && search.trim()) queryParams.append("search", search.trim());
  if (status && status !== "all") queryParams.append("status", status.trim());

  const baseQuery = queryParams.toString() ? `?${queryParams.toString()}` : "";
  const genericQuery = queryParams.toString()
    ? `?${queryParams.toString()}&game=slots&gameType=slots`
    : "?game=slots&gameType=slots";

  const endpoints = [
    `${baseUrl}/game/slots/history${baseQuery}`,
    `${baseUrl}/game/history${genericQuery}`,
    `${baseUrl}/game/slots/logs${baseQuery}`,
  ];

  let lastResponse;
  for (const url of endpoints) {
    try {
      const response = await fetch(url, {
        method: "GET",
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        return handleApiResponse(response);
      }
      lastResponse = response;
      if (response.status !== 404) {
        return handleApiResponse(response);
      }
    } catch (netErr) {
      if (url === endpoints[endpoints.length - 1]) {
        throw new Error(netErr.message || "Network error connecting to API");
      }
    }
  }

  return handleApiResponse(lastResponse);
};

// ==========================================
// 🎡 1.6. LUCKY SPIN WHEEL API ENDPOINTS
// ==========================================

/**
 * Fetch Lucky Spin Wheel Settings & Statistics
 * Endpoint: GET /admin/game/spinwheel/settings (fallback: /admin/game/spin/settings)
 */
export const getSpinwheelSettings = async () => {
  const baseUrl = getAdminBaseUrl();
  const endpoints = [
    `${baseUrl}/game/spinwheel/settings`,
    `${baseUrl}/game/spin/settings`,
    `${baseUrl}/game/spinwheel`,
    `${baseUrl}/game/settings?game=spinwheel`,
  ];

  let lastResponse;
  for (const url of endpoints) {
    try {
      const response = await fetch(url, {
        method: "GET",
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        return handleApiResponse(response);
      }
      lastResponse = response;
      if (response.status !== 404) {
        return handleApiResponse(response);
      }
    } catch (netErr) {
      if (url === endpoints[endpoints.length - 1]) {
        throw new Error(netErr.message || "Network error connecting to Spin Wheel API");
      }
    }
  }

  return handleApiResponse(lastResponse);
};

/**
 * Update Lucky Spin Wheel Game Settings
 * Endpoint: PUT /admin/game/spinwheel/settings (fallback: /admin/game/spin/settings)
 * @param {Object} settingsData
 */
export const updateSpinwheelSettings = async (settingsData) => {
  const baseUrl = getAdminBaseUrl();
  const payload = {
    slices: Array.isArray(settingsData.slices)
      ? settingsData.slices.map(Number)
      : [0, 10, 0, 50, 0, 100, 0, 500, 0, 1000],
    spinTime: Number(settingsData.spinTime ?? settingsData.gameTime ?? 5),
    gameTime: Number(settingsData.spinTime ?? settingsData.gameTime ?? 5),
    cooldownSeconds: Number(settingsData.cooldownSeconds ?? 180),
    minPoints: Number(settingsData.minPoints ?? 0),
    maxPoints: Number(settingsData.maxPoints ?? 1000),
    isActive: typeof settingsData.isActive === "boolean" ? settingsData.isActive : Boolean(settingsData.isActive),
    game: "spinwheel",
    gameType: "spinwheel",
  };

  if (settingsData.name) payload.name = settingsData.name;
  if (settingsData.description) payload.description = settingsData.description;
  if (settingsData.pointProbabilities) payload.pointProbabilities = settingsData.pointProbabilities;

  const endpoints = [
    { url: `${baseUrl}/game/spinwheel/settings`, method: "PUT" },
    { url: `${baseUrl}/game/spinwheel/settings`, method: "POST" },
    { url: `${baseUrl}/game/spin/settings`, method: "PUT" },
    { url: `${baseUrl}/game/spinwheel`, method: "PUT" },
    { url: `${baseUrl}/game/spinwheel`, method: "PATCH" },
  ];

  let lastResponse;
  for (const item of endpoints) {
    try {
      const response = await fetch(item.url, {
        method: item.method,
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      });
      if (response.ok) {
        return handleApiResponse(response);
      }
      lastResponse = response;
      if (response.status !== 404 && response.status !== 405) {
        return handleApiResponse(response);
      }
    } catch (netErr) {
      if (item === endpoints[endpoints.length - 1]) {
        throw new Error(netErr.message || "Network error updating Spin Wheel settings");
      }
    }
  }

  return handleApiResponse(lastResponse);
};

/**
 * Get Lucky Spin Wheel Audit History Logs
 * Endpoint: GET /admin/game/spinwheel/history (fallback: /admin/game/spin/history)
 * @param {Object} params
 */
export const getSpinwheelHistory = async ({
  page = 1,
  perPage = 20,
  search = "",
  status = "",
  startDate = "",
  endDate = "",
} = {}) => {
  const baseUrl = getAdminBaseUrl();
  const queryParams = new URLSearchParams();

  if (page) queryParams.append("page", String(page));
  if (perPage) {
    queryParams.append("perPage", String(perPage));
    queryParams.append("limit", String(perPage));
  }
  if (search && search.trim()) queryParams.append("search", search.trim());
  if (status && status !== "all") queryParams.append("status", status.trim());
  if (startDate) queryParams.append("startDate", startDate);
  if (endDate) queryParams.append("endDate", endDate);

  const baseQuery = queryParams.toString() ? `?${queryParams.toString()}` : "";
  const genericQuery = queryParams.toString()
    ? `?${queryParams.toString()}&game=spinwheel&gameType=spinwheel`
    : "?game=spinwheel&gameType=spinwheel";

  const endpoints = [
    `${baseUrl}/game/spinwheel/history${baseQuery}`,
    `${baseUrl}/game/spin/history${baseQuery}`,
    `${baseUrl}/game/history${genericQuery}`,
  ];

  let lastResponse;
  for (const url of endpoints) {
    try {
      const response = await fetch(url, {
        method: "GET",
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        return handleApiResponse(response);
      }
      lastResponse = response;
      if (response.status !== 404) {
        return handleApiResponse(response);
      }
    } catch (netErr) {
      if (url === endpoints[endpoints.length - 1]) {
        throw new Error(netErr.message || "Network error connecting to Spin Wheel history API");
      }
    }
  }

  return handleApiResponse(lastResponse);
};

// ==========================================
// 🎮 2. TAP GAME API ENDPOINTS
// ==========================================

/**
 * Fetch Tap Game Settings & Statistics
 * Endpoint: GET /admin/game/tap/settings (fallback: /admin/game/settings?game=tap)
 */
export const getTapGameSettings = async () => {
  const baseUrl = getAdminBaseUrl();
  const endpoints = [
    `${baseUrl}/game/tap/settings`,
    `${baseUrl}/game/settings?game=tap`,
    `${baseUrl}/game/tap`,
    `${baseUrl}/game/settings`,
  ];

  let lastResponse;
  for (const url of endpoints) {
    try {
      const response = await fetch(url, {
        method: "GET",
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        return handleApiResponse(response);
      }
      lastResponse = response;
      if (response.status !== 404) {
        return handleApiResponse(response);
      }
    } catch (netErr) {
      if (url === endpoints[endpoints.length - 1]) {
        throw new Error(netErr.message || "Network error connecting to API");
      }
    }
  }

  return handleApiResponse(lastResponse);
};

/**
 * Update Tap Game Settings
 * Endpoint: PUT /admin/game/tap/settings (fallback: /admin/game/settings?game=tap)
 * @param {Object} settingsData
 */
export const updateTapGameSettings = async (settingsData) => {
  const baseUrl = getAdminBaseUrl();
  const payload = {
    ...settingsData,
    game: "tap",
    gameType: "tap",
  };
  if (typeof payload.isActive === "boolean") {
    payload.isActive = payload.isActive ? 1 : 0;
  }

  const endpoints = [
    `${baseUrl}/game/tap/settings`,
    `${baseUrl}/game/settings?game=tap`,
    `${baseUrl}/game/tap`,
    `${baseUrl}/game/settings`,
  ];

  let lastResponse;
  for (const url of endpoints) {
    try {
      const response = await fetch(url, {
        method: "PUT",
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      });
      if (response.ok) {
        return handleApiResponse(response);
      }
      lastResponse = response;
      if (response.status !== 404) {
        return handleApiResponse(response);
      }
    } catch (netErr) {
      if (url === endpoints[endpoints.length - 1]) {
        throw new Error(netErr.message || "Network error connecting to API");
      }
    }
  }

  return handleApiResponse(lastResponse);
};

/**
 * Get Tap Game Audit History Logs
 * Endpoint: GET /admin/game/tap/history (fallback: /admin/game/history?game=tap)
 * @param {Object} params
 */
export const getTapGameHistory = async ({
  page = 1,
  perPage = 20,
  search = "",
  status = "",
} = {}) => {
  const baseUrl = getAdminBaseUrl();
  const queryParams = new URLSearchParams();

  if (page) queryParams.append("page", String(page));
  if (perPage) {
    queryParams.append("perPage", String(perPage));
    queryParams.append("limit", String(perPage));
  }
  if (search && search.trim()) queryParams.append("search", search.trim());
  if (status && status !== "all") queryParams.append("status", status.trim());

  const baseQuery = queryParams.toString() ? `?${queryParams.toString()}` : "";
  const genericQuery = queryParams.toString()
    ? `?${queryParams.toString()}&game=tap&gameType=tap`
    : "?game=tap&gameType=tap";

  const endpoints = [
    `${baseUrl}/game/tap/history${baseQuery}`,
    `${baseUrl}/game/history${genericQuery}`,
    `${baseUrl}/game/history${baseQuery}`,
  ];

  let lastResponse;
  for (const url of endpoints) {
    try {
      const response = await fetch(url, {
        method: "GET",
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        return handleApiResponse(response);
      }
      lastResponse = response;
      if (response.status !== 404) {
        return handleApiResponse(response);
      }
    } catch (netErr) {
      if (url === endpoints[endpoints.length - 1]) {
        throw new Error(netErr.message || "Network error connecting to API");
      }
    }
  }

  return handleApiResponse(lastResponse);
};

// ==========================================
// 🎮 3. UNIFIED GAMES MANAGEMENT API (README SPECS)
// ==========================================

export const fetchAdminGames = async (params = {}) => {
  const baseUrl = getAdminBaseUrl();
  const queryParams = new URLSearchParams();

  if (params.status !== undefined && params.status !== "all" && params.status !== "") {
    queryParams.append("status", String(params.status));
  }
  if (params.search && params.search.trim()) {
    queryParams.append("search", params.search.trim());
  }

  const queryString = queryParams.toString() ? `?${queryParams.toString()}` : "";
  const endpoints = [
    `${baseUrl}/game/list${queryString}`,
    `${baseUrl}/game${queryString}`,
    `${baseUrl}/games${queryString}`,
  ];

  let lastResponse;
  for (const url of endpoints) {
    try {
      const response = await fetch(url, {
        method: "GET",
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        return handleApiResponse(response);
      }
      lastResponse = response;
      if (response.status !== 404) {
        return handleApiResponse(response);
      }
    } catch (netErr) {
      if (url === endpoints[endpoints.length - 1]) {
        throw new Error(netErr.message || "Network error connecting to Games API");
      }
    }
  }

  return handleApiResponse(lastResponse);
};

export const saveGameOrder = async (reorderedGamesList) => {
  const baseUrl = getAdminBaseUrl();
  const formattedGames = reorderedGamesList.map((game, index) => ({
    id: game.id,
    gameKey: game.gameKey || game.key || game.route,
    sortOrder: index + 1,
  }));
  const orderArray = reorderedGamesList.map((g) => g.id).filter(Boolean);

  const payload = {
    games: formattedGames,
    order: orderArray,
  };

  const endpoints = [
    { url: `${baseUrl}/game/reorder`, method: "PUT" },
    { url: `${baseUrl}/game/reorder`, method: "POST" },
    { url: `${baseUrl}/games/reorder`, method: "PUT" },
  ];

  let lastResponse;
  for (const item of endpoints) {
    try {
      const response = await fetch(item.url, {
        method: item.method,
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      });
      if (response.ok) {
        return handleApiResponse(response);
      }
      lastResponse = response;
      if (response.status !== 404 && response.status !== 405) {
        return handleApiResponse(response);
      }
    } catch (netErr) {
      if (item === endpoints[endpoints.length - 1]) {
        throw new Error(netErr.message || "Network error while saving game order");
      }
    }
  }

  return handleApiResponse(lastResponse);
};

export const updateSingleGameOrder = async (gameIdOrKey, sortOrder) => {
  const baseUrl = getAdminBaseUrl();
  const payload = { sortOrder: Number(sortOrder) };

  const endpoints = [
    { url: `${baseUrl}/game/${gameIdOrKey}/order`, method: "PATCH" },
    { url: `${baseUrl}/game/${gameIdOrKey}/order`, method: "PUT" },
    { url: `${baseUrl}/game/order/${gameIdOrKey}`, method: "PATCH" },
  ];

  let lastResponse;
  for (const item of endpoints) {
    try {
      const response = await fetch(item.url, {
        method: item.method,
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      });
      if (response.ok) {
        return handleApiResponse(response);
      }
      lastResponse = response;
      if (response.status !== 404 && response.status !== 405) {
        return handleApiResponse(response);
      }
    } catch (netErr) {
      if (item === endpoints[endpoints.length - 1]) {
        throw new Error(netErr.message || "Network error updating game order");
      }
    }
  }

  return handleApiResponse(lastResponse);
};

export const toggleGameActiveStatus = async (gameIdOrKey, newStatus) => {
  const baseUrl = getAdminBaseUrl();
  const payload = typeof newStatus === "boolean" ? { isActive: newStatus } : {};

  const endpoints = [
    { url: `${baseUrl}/game/${gameIdOrKey}/status`, method: "PATCH" },
    { url: `${baseUrl}/game/${gameIdOrKey}/status`, method: "PUT" },
    { url: `${baseUrl}/game/status/${gameIdOrKey}`, method: "PATCH" },
    { url: `${baseUrl}/game/${gameIdOrKey}/toggle`, method: "POST" },
  ];

  let lastResponse;
  for (const item of endpoints) {
    try {
      const response = await fetch(item.url, {
        method: item.method,
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      });
      if (response.ok) {
        return handleApiResponse(response);
      }
      lastResponse = response;
      if (response.status !== 404 && response.status !== 405) {
        return handleApiResponse(response);
      }
    } catch (netErr) {
      if (item === endpoints[endpoints.length - 1]) {
        throw new Error(netErr.message || "Network error toggling game status");
      }
    }
  }

  return handleApiResponse(lastResponse);
};

export const updateGameSettings = async (gameIdOrKey, formData) => {
  const baseUrl = getAdminBaseUrl();
  const payload = {
    name: formData.name,
    description: formData.description,
    gameTime: Number(formData.gameTime),
    minPoints: Number(formData.minPoints),
    maxPoints: Number(formData.maxPoints),
    cooldownSeconds: Number(formData.cooldownSeconds),
    isActive: Boolean(formData.isActive === 1 || formData.isActive === true || formData.isActive === "1"),
    ...(formData.sortOrder !== undefined ? { sortOrder: Number(formData.sortOrder) } : {}),
    ...(formData.icon ? { icon: formData.icon } : {}),
    ...(formData.banner ? { banner: formData.banner } : {}),
  };

  const endpoints = [
    { url: `${baseUrl}/game/${gameIdOrKey}`, method: "PUT" },
    { url: `${baseUrl}/game/${gameIdOrKey}`, method: "PATCH" },
    { url: `${baseUrl}/game/${gameIdOrKey}/settings`, method: "PUT" },
  ];

  let lastResponse;
  for (const item of endpoints) {
    try {
      const response = await fetch(item.url, {
        method: item.method,
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      });
      if (response.ok) {
        return handleApiResponse(response);
      }
      lastResponse = response;
      if (response.status !== 404 && response.status !== 405) {
        return handleApiResponse(response);
      }
    } catch (netErr) {
      if (item === endpoints[endpoints.length - 1]) {
        throw new Error(netErr.message || "Network error saving game settings");
      }
    }
  }

  return handleApiResponse(lastResponse);
};

export const getGameAuditHistory = async (
  gameKey = "tap",
  { page = 1, perPage = 20, search = "", status = "" } = {}
) => {
  const baseUrl = getAdminBaseUrl();
  const queryParams = new URLSearchParams();

  if (page) queryParams.append("page", String(page));
  if (perPage) {
    queryParams.append("perPage", String(perPage));
    queryParams.append("limit", String(perPage));
  }
  if (search && search.trim()) queryParams.append("search", search.trim());
  if (status && status !== "all") queryParams.append("status", status.trim());

  const queryString = queryParams.toString() ? `?${queryParams.toString()}` : "";
  const genericQuery = queryParams.toString()
    ? `?${queryParams.toString()}&game=${gameKey}&gameType=${gameKey}`
    : `?game=${gameKey}&gameType=${gameKey}`;

  const endpoints = [
    `${baseUrl}/game/${gameKey}/history${queryString}`,
    `${baseUrl}/game/history${genericQuery}`,
    `${baseUrl}/game/${gameKey}/logs${queryString}`,
  ];

  let lastResponse;
  for (const url of endpoints) {
    try {
      const response = await fetch(url, {
        method: "GET",
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        return handleApiResponse(response);
      }
      lastResponse = response;
      if (response.status !== 404) {
        return handleApiResponse(response);
      }
    } catch (netErr) {
      if (url === endpoints[endpoints.length - 1]) {
        throw new Error(netErr.message || "Network error retrieving game history");
      }
    }
  }

  return handleApiResponse(lastResponse);
};


